import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Wallet, Clock, ImageIcon, Sparkles, Save, MapPinned, ImagePlus, Video, Trash2, Eye, Star, Phone, Mail, ExternalLink } from 'lucide-react';
import { getMyGym, createGym, updateMyGym, deleteMyGym, uploadCoverImage, uploadGalleryImages, deleteGalleryImage, uploadVideo, deleteVideo } from '../api/gymApi';
import { useToast } from '../components/ToastProvider';
import SlotGenerator from '../components/SlotGenerator';
import MapLocationPicker from '../components/MapLocationPicker';
import { inp, Field, SectionCard, sanitizeWeeklySlots, WEEKDAYS, generateSlots, MultiSelectDropdown } from '../components/FormKit';

const GYM_TYPES = ['standard', 'premium', 'budget', 'boutique'];
const FEATURE_OPTIONS = ['24x7 Access', 'Personal Trainer', 'Group Classes', 'Nutrition Counseling', 'Steam Room', 'Diet Plan', 'Online Booking', 'Free Trial'];
const FACILITY_OPTIONS = ['cardio', 'weights', 'crossfit', 'yoga', 'swimming', 'zumba', 'boxing', 'functional training'];
const AMENITY_OPTIONS = ['Parking', 'Showers', 'Locker', 'AC', 'WiFi', 'Cafe', 'Changing Room', 'Drinking Water'];
const EQUIPMENT_OPTIONS = ['Treadmills', 'Squat Racks', 'Cable Machines', 'Dumbbells', 'Rowing Machines', 'Barbells', 'Kettlebells', 'Smith Machine'];
const MAX_GALLERY_IMAGES = 8;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const VIDEO_MAX_SIZE_MB = 100;

const TABS = [
  { id: 'basic', label: 'Basic Info', icon: Building2 },
  { id: 'location', label: 'Location', icon: MapPin },
  { id: 'pricing', label: 'Pricing', icon: Wallet },
  { id: 'slots', label: 'Slots Info', icon: Clock },
  { id: 'images', label: 'Images', icon: ImageIcon },
  { id: 'extras', label: 'Extras', icon: Sparkles },
];

const EMPTY_FORM = {
  name: '', tagline: '', description: '', gymType: 'standard', phone: '', email: '', website: '',
  address: '', city: '', state: '', zipCode: '', longitude: '', latitude: '',
  dailyPassPrice: '', weeklyPassPrice: '', monthlyPassPrice: '', currency: 'INR',
  weeklySlots: WEEKDAYS.reduce((acc, d) => ({ ...acc, [d]: [] }), {}),
  features: [], facilities: [], amenities: [], equipment: [], rules: '',
  yearsInBusiness: '',
  slotGenerator: { day: 'monday', openTime: '06:00', closeTime: '22:00', durationMinutes: 90, capacity: 30, bulkMode: false, bulkDays: [] },
  slotGeneratorTouched: false,
};

function gymToForm(gym) {
  return {
    ...EMPTY_FORM,
    ...gym,
    longitude: gym.location?.coordinates?.[0] ?? '',
    latitude: gym.location?.coordinates?.[1] ?? '',
    weeklySlots: { ...EMPTY_FORM.weeklySlots, ...(gym.weeklySlots || {}) },
    features: gym.features || [],
    facilities: gym.facilities || [],
    amenities: gym.amenities || [],
    equipment: gym.equipment || [],
    rules: (gym.rules || []).join(', '),
    slotGenerator: EMPTY_FORM.slotGenerator,
    slotGeneratorTouched: false,
  };
}

function formToPayload(form) {
  const toList = s => String(s || '').split(',').map(x => x.trim()).filter(Boolean);
  const gen = form.slotGenerator;
  const generated = () => generateSlots(gen.openTime, gen.closeTime, gen.durationMinutes, gen.capacity);
  const weeklySlots = form.slotGeneratorTouched
    ? gen.bulkMode
      ? {
          ...form.weeklySlots,
          ...Object.fromEntries((gen.bulkDays || []).map(day => [day, generated()])),
        }
      : { ...form.weeklySlots, [gen.day]: generated() }
    : form.weeklySlots;

  return {
    name: form.name, tagline: form.tagline, description: form.description, gymType: form.gymType,
    phone: form.phone, email: form.email, website: form.website,
    address: form.address, city: form.city, state: form.state, zipCode: form.zipCode,
    location: { type: 'Point', coordinates: [Number(form.longitude) || 0, Number(form.latitude) || 0] },
    dailyPassPrice: Number(form.dailyPassPrice) || 0,
    weeklyPassPrice: Number(form.weeklyPassPrice) || 0,
    monthlyPassPrice: Number(form.monthlyPassPrice) || 0,
    currency: form.currency,
    weeklySlots: sanitizeWeeklySlots(weeklySlots, WEEKDAYS),
    features: form.features,
    facilities: form.facilities,
    amenities: form.amenities,
    equipment: form.equipment,
    rules: toList(form.rules),
    yearsInBusiness: Number(form.yearsInBusiness) || 0,
  };
}

function applyPickedLocation(setForm, picked) {
  setForm(f => ({
    ...f,
    latitude: picked.latitude,
    longitude: picked.longitude,
    ...(picked.address ? { address: picked.address } : {}),
    ...(picked.city ? { city: picked.city } : {}),
    ...(picked.state ? { state: picked.state } : {}),
    ...(picked.zipCode ? { zipCode: picked.zipCode } : {}),
  }));
}

function CreateGymForm({ onCreated }) {
  const { showToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const gym = await createGym(formToPayload(form));
      showToast('Gym created!');
      onCreated(gym);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create gym', 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex flex-col items-center mb-6">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
          style={{ background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 60%, #8ab4f8 100%)', boxShadow: '0 8px 24px rgba(26,115,232,0.35)' }}
        >
          <Building2 className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">List Your Gym</h1>
        <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-0.5">Fill in the essentials to get started — you can add more details later.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <SectionCard title="Essentials" icon={<Building2 className="w-4 h-4" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Gym Name"><input name="name" required value={form.name} onChange={handleChange} className={inp} placeholder="Iron Paradise" /></Field>
            <Field label="Phone"><input name="phone" required value={form.phone} onChange={handleChange} className={inp} placeholder="9876543210" /></Field>
            <Field label="Address"><input name="address" required value={form.address} onChange={handleChange} className={inp} placeholder="123 Main St" /></Field>
            <Field label="City"><input name="city" required value={form.city} onChange={handleChange} className={inp} placeholder="Mumbai" /></Field>
            <Field label="State"><input name="state" required value={form.state} onChange={handleChange} className={inp} placeholder="MH" /></Field>
            <Field label="Daily Pass Price (₹)"><input type="number" name="dailyPassPrice" required value={form.dailyPassPrice} onChange={handleChange} className={inp} placeholder="250" /></Field>
            <Field label="Longitude"><input type="number" step="any" name="longitude" required value={form.longitude} onChange={handleChange} className={inp} placeholder="72.8777" /></Field>
            <Field label="Latitude"><input type="number" step="any" name="latitude" required value={form.latitude} onChange={handleChange} className={inp} placeholder="19.0760" /></Field>
            <div className="sm:col-span-2">
              <button type="button" onClick={() => setShowPicker(true)}
                className="text-xs font-semibold px-3.5 py-2 rounded-full border border-[#dadce0] dark:border-[#3c4043] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-[#1a2e4c] cursor-pointer flex items-center gap-1.5">
                <MapPinned className="w-3.5 h-3.5" /> Pick Location on Map
              </button>
            </div>
          </div>
        </SectionCard>

        <button type="submit" disabled={loading}
          className="w-full py-3.5 rounded-full text-sm font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white shadow-xs transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" />
          {loading ? 'Creating...' : 'Create Gym Listing'}
        </button>
      </form>

      {showPicker && (
        <MapLocationPicker
          initialLat={form.latitude}
          initialLng={form.longitude}
          onClose={() => setShowPicker(false)}
          onConfirm={picked => { applyPickedLocation(setForm, picked); setShowPicker(false); }}
        />
      )}
    </div>
  );
}

function ImagesSection({ gym, onUpdated }) {
  const { showToast } = useToast();
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  function validateFiles(files) {
    for (const file of files) {
      if (!IMAGE_TYPES.includes(file.type)) {
        showToast('Please choose JPEG, PNG, or WEBP images.', 'error');
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Each image must be smaller than 5MB.', 'error');
        return false;
      }
    }
    return true;
  }

  async function handleCoverChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !validateFiles([file])) return;

    setCoverUploading(true);
    try {
      const updated = await uploadCoverImage(file);
      onUpdated(updated);
      showToast('Cover image updated');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to upload cover image', 'error');
    } finally {
      setCoverUploading(false);
    }
  }

  async function handleGalleryChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !validateFiles(files)) return;

    if (gym.images.length + files.length > MAX_GALLERY_IMAGES) {
      showToast(`A gym can have at most ${MAX_GALLERY_IMAGES} gallery images (currently has ${gym.images.length})`, 'error');
      return;
    }

    setGalleryUploading(true);
    try {
      const updated = await uploadGalleryImages(files);
      onUpdated(updated);
      showToast('Gallery images uploaded');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to upload gallery images', 'error');
    } finally {
      setGalleryUploading(false);
    }
  }

  async function handleDeleteGalleryImage(index) {
    setDeletingIndex(index);
    try {
      const updated = await deleteGalleryImage(index);
      onUpdated(updated);
      showToast('Image removed');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove image', 'error');
    } finally {
      setDeletingIndex(null);
    }
  }

  return (
    <>
      <SectionCard title="Cover Image" icon={<ImageIcon className="w-4 h-4" />}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-full sm:w-56 h-36 rounded-2xl overflow-hidden shrink-0 bg-[#f1f3f4] dark:bg-[#202124] flex items-center justify-center relative">
            {gym.coverImage ? (
              <img src={gym.coverImage} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-[#9aa0a6]">No cover image</span>
            )}
            {coverUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <span className="text-white text-xs font-semibold">Uploading...</span>
              </div>
            )}
          </div>
          <div>
            <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverChange} className="hidden" />
            <button type="button" onClick={() => coverInputRef.current?.click()} disabled={coverUploading}
              className="px-4 py-2 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-[#1a2e4c] cursor-pointer flex items-center gap-1.5 disabled:opacity-60">
              <ImagePlus className="w-3.5 h-3.5" /> {gym.coverImage ? 'Change Cover Image' : 'Upload Cover Image'}
            </button>
            <p className="text-[11px] text-[#9aa0a6] mt-2">JPEG, PNG, or WEBP — up to 5MB.</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={`Gallery Images (${gym.images.length}/${MAX_GALLERY_IMAGES})`} icon={<ImageIcon className="w-4 h-4" />}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {gym.images.map((url, i) => (
            <div key={url} className="relative aspect-square rounded-xl overflow-hidden bg-[#f1f3f4] dark:bg-[#202124] group">
              <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
              <button type="button" onClick={() => handleDeleteGalleryImage(i)} disabled={deletingIndex === i}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:opacity-60">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {gym.images.length < MAX_GALLERY_IMAGES && (
            <button type="button" onClick={() => galleryInputRef.current?.click()} disabled={galleryUploading}
              className="aspect-square rounded-xl border-2 border-dashed border-[#dadce0] dark:border-[#3c4043] flex flex-col items-center justify-center gap-1 text-[#9aa0a6] hover:border-[#1a73e8] hover:text-[#1a73e8] cursor-pointer disabled:opacity-60">
              <ImagePlus className="w-5 h-5" />
              <span className="text-[11px] font-medium">{galleryUploading ? 'Uploading...' : 'Add Images'}</span>
            </button>
          )}
        </div>
        <input ref={galleryInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleGalleryChange} className="hidden" />
        <p className="text-[11px] text-[#9aa0a6] mt-3">JPEG, PNG, or WEBP — up to 5MB each, {MAX_GALLERY_IMAGES} images max.</p>
      </SectionCard>
    </>
  );
}

function VideoSection({ gym, onUpdated }) {
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const videoInputRef = useRef(null);

  async function handleVideoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!VIDEO_TYPES.includes(file.type)) {
      showToast('Please choose an MP4, WEBM, or MOV video.', 'error');
      return;
    }
    if (file.size > VIDEO_MAX_SIZE_MB * 1024 * 1024) {
      showToast(`Video must be smaller than ${VIDEO_MAX_SIZE_MB}MB.`, 'error');
      return;
    }

    setUploading(true);
    try {
      const updated = await uploadVideo(file);
      onUpdated(updated);
      showToast('Video uploaded');
    } catch (err) {
      // err may be a plain Error (the direct GCS PUT failing) or an axios
      // error (the upload-url/confirm calls) — check both shapes.
      showToast(err.response?.data?.message || err.message || 'Failed to upload video', 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteVideo() {
    setDeleting(true);
    try {
      const updated = await deleteVideo();
      onUpdated(updated);
      showToast('Video removed');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove video', 'error');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <SectionCard title="Video" icon={<Video className="w-4 h-4" />}>
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <div className="w-full sm:w-64 aspect-video rounded-2xl overflow-hidden shrink-0 bg-[#f1f3f4] dark:bg-[#202124] flex items-center justify-center relative">
          {gym.video ? (
            <video src={gym.video} controls className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs text-[#9aa0a6]">No video</span>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <span className="text-white text-xs font-semibold">Uploading...</span>
            </div>
          )}
        </div>
        <div>
          <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleVideoChange} className="hidden" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => videoInputRef.current?.click()} disabled={uploading}
              className="px-4 py-2 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-[#1a2e4c] cursor-pointer flex items-center gap-1.5 disabled:opacity-60">
              <Video className="w-3.5 h-3.5" /> {gym.video ? 'Replace Video' : 'Upload Video'}
            </button>
            {gym.video && (
              <button type="button" onClick={handleDeleteVideo} disabled={deleting}
                className="px-4 py-2 rounded-full text-xs font-semibold border border-red-200 dark:border-red-900 text-[#EA4335] hover:bg-red-50 dark:hover:bg-red-950/30 cursor-pointer disabled:opacity-60 flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> {deleting ? '...' : 'Remove Video'}
              </button>
            )}
          </div>
          <p className="text-[11px] text-[#9aa0a6] mt-2">MP4, WEBM, or MOV — up to {VIDEO_MAX_SIZE_MB}MB. Only one video per gym; uploading a new one replaces it.</p>
        </div>
      </div>
    </SectionCard>
  );
}

function EditGymForm({ gym, onUpdated }) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => gymToForm(gym));
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateMyGym(formToPayload(form));
      showToast('Gym updated');
      onUpdated(updated);
      setForm(f => ({ ...f, slotGenerator: EMPTY_FORM.slotGenerator, slotGeneratorTouched: false }));
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update gym', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    setTogglingActive(true);
    try {
      if (gym.isActive) {
        const updated = await deleteMyGym();
        showToast('Gym deactivated');
        onUpdated(updated);
      } else {
        const updated = await updateMyGym({ isActive: true });
        showToast('Gym reactivated');
        onUpdated(updated);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Action failed', 'error');
    } finally {
      setTogglingActive(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      {/* Main column — the editor */}
      <div className="lg:col-span-8 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">{gym.name}</h2>
            <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">{gym.city}, {gym.state}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/listing-preview')}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] cursor-pointer flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Full Preview
            </button>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
              gym.isActive ? 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300' : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6]'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${gym.isActive ? 'bg-[#34A853]' : 'bg-[#9aa0a6]'}`} />
              {gym.isActive ? 'Active' : 'Inactive'}
            </span>
            <button
              onClick={handleToggleActive}
              disabled={togglingActive}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134] cursor-pointer disabled:opacity-60">
              {togglingActive ? '...' : gym.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-[#e8eaed] dark:border-[#3c4043] pb-2 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8] font-semibold'
                    : 'text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#f1f3f4] dark:hover:bg-[#303134]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {activeTab === 'basic' && (
            <SectionCard title="Basic Info" icon={<Building2 className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Gym Name"><input name="name" value={form.name} onChange={handleChange} className={inp} /></Field>
                <Field label="Gym Type">
                  <select name="gymType" value={form.gymType} onChange={handleChange} className={inp + ' cursor-pointer'}>
                    {GYM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Tagline"><input name="tagline" value={form.tagline} onChange={handleChange} className={inp} /></Field>
                <Field label="Phone"><input name="phone" value={form.phone} onChange={handleChange} className={inp} /></Field>
                <Field label="Email"><input type="email" name="email" value={form.email} onChange={handleChange} className={inp} /></Field>
                <Field label="Website"><input name="website" value={form.website} onChange={handleChange} className={inp} /></Field>
                <div className="sm:col-span-2">
                  <Field label="Description">
                    <textarea name="description" value={form.description} onChange={handleChange} rows={3} className={inp + ' resize-none'} />
                  </Field>
                </div>
              </div>
            </SectionCard>
          )}

          {activeTab === 'location' && (
            <SectionCard title="Location" icon={<MapPin className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Field label="Address"><input name="address" value={form.address} onChange={handleChange} className={inp} /></Field>
                </div>
                <Field label="City"><input name="city" value={form.city} onChange={handleChange} className={inp} /></Field>
                <Field label="State"><input name="state" value={form.state} onChange={handleChange} className={inp} /></Field>
                <Field label="Zip Code"><input name="zipCode" value={form.zipCode} onChange={handleChange} className={inp} /></Field>
                <div />
                <Field label="Longitude"><input type="number" step="any" name="longitude" value={form.longitude} onChange={handleChange} className={inp} /></Field>
                <Field label="Latitude"><input type="number" step="any" name="latitude" value={form.latitude} onChange={handleChange} className={inp} /></Field>
                <div className="sm:col-span-2">
                  <button type="button" onClick={() => setShowPicker(true)}
                    className="text-xs font-semibold px-3.5 py-2 rounded-full border border-[#dadce0] dark:border-[#3c4043] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-[#1a2e4c] cursor-pointer flex items-center gap-1.5">
                    <MapPinned className="w-3.5 h-3.5" /> Pick Location on Map
                  </button>
                </div>
              </div>
            </SectionCard>
          )}

          {activeTab === 'pricing' && (
            <SectionCard title="Pricing" icon={<Wallet className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Daily Pass Price"><input type="number" name="dailyPassPrice" value={form.dailyPassPrice} onChange={handleChange} className={inp} /></Field>
                <Field label="Currency"><input name="currency" value={form.currency} onChange={handleChange} className={inp} /></Field>
                <Field label="Weekly Pass Price"><input type="number" name="weeklyPassPrice" value={form.weeklyPassPrice} onChange={handleChange} className={inp} /></Field>
                <Field label="Monthly Pass Price"><input type="number" name="monthlyPassPrice" value={form.monthlyPassPrice} onChange={handleChange} className={inp} /></Field>
              </div>
            </SectionCard>
          )}

          {activeTab === 'slots' && (
            <SectionCard title="Slots Info" icon={<Clock className="w-4 h-4" />}>
              <SlotGenerator
                weeklySlots={form.weeklySlots}
                config={form.slotGenerator}
                onConfigChange={next => setForm(f => ({ ...f, slotGenerator: next, slotGeneratorTouched: true }))}
              />
            </SectionCard>
          )}

          {activeTab === 'images' && (
            <div className="space-y-4">
              <ImagesSection gym={gym} onUpdated={onUpdated} />
              <VideoSection gym={gym} onUpdated={onUpdated} />
            </div>
          )}

          {activeTab === 'extras' && (
            <SectionCard title="Extras" icon={<Sparkles className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MultiSelectDropdown label="Features" options={FEATURE_OPTIONS} selected={form.features}
                  onChange={vals => setForm(f => ({ ...f, features: vals }))} />
                <MultiSelectDropdown label="Facilities" options={FACILITY_OPTIONS} selected={form.facilities}
                  onChange={vals => setForm(f => ({ ...f, facilities: vals }))} />
                <MultiSelectDropdown label="Amenities" options={AMENITY_OPTIONS} selected={form.amenities}
                  onChange={vals => setForm(f => ({ ...f, amenities: vals }))} />
                <MultiSelectDropdown label="Equipment" options={EQUIPMENT_OPTIONS} selected={form.equipment}
                  onChange={vals => setForm(f => ({ ...f, equipment: vals }))} />
                <Field label="Rules (comma-separated)"><input name="rules" value={form.rules} onChange={handleChange} className={inp} /></Field>
                <Field label="Years in Business"><input type="number" name="yearsInBusiness" value={form.yearsInBusiness} onChange={handleChange} className={inp} /></Field>
              </div>
            </SectionCard>
          )}

          <button type="submit" disabled={saving}
            className="w-full py-3.5 rounded-full text-sm font-semibold bg-[#34A853] hover:bg-[#2d8e47] text-white shadow-xs transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Live preview */}
      <div className="lg:col-span-4 lg:sticky lg:top-6">
        <div className="google-card bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] overflow-hidden">
          <div className="h-28 bg-gradient-to-br from-[#1a73e8] via-[#4285f4] to-[#8ab4f8] relative">
            {form.coverImage && <img src={form.coverImage} alt={form.name} className="absolute inset-0 w-full h-full object-cover" />}
            <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-black/30 text-white backdrop-blur-sm">Live Preview</span>
          </div>
          <div className="p-4">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8] capitalize">{form.gymType}</span>
            <h3 className="text-base font-bold text-[#202124] dark:text-[#e8eaed] mt-1.5 truncate">{form.name || 'Untitled Gym'}</h3>
            {form.tagline && <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-0.5 truncate">{form.tagline}</p>}

            <div className="flex items-center gap-1 mt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(gym.averageRating || 0) ? 'fill-[#FBBC05] text-[#FBBC05]' : 'text-[#dadce0] dark:text-[#3c4043]'}`} />
              ))}
              <span className="text-[11px] text-[#9aa0a6] ml-1">({gym.totalReviews || 0})</span>
            </div>

            <div className="flex items-center gap-1 text-[11px] text-[#5f6368] dark:text-[#9aa0a6] mt-2">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{form.city}, {form.state}</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-[#5f6368] dark:text-[#9aa0a6] mt-1">
              <Phone className="w-3 h-3 shrink-0" />
              <span className="truncate">{form.phone || 'No phone set'}</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-[#5f6368] dark:text-[#9aa0a6] mt-1">
              <Mail className="w-3 h-3 shrink-0" />
              <span className="truncate">{form.email || 'No email set'}</span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#e6f4ea] text-[#1e8e3e]">₹{form.dailyPassPrice || 0}/day</span>
              {form.amenities.slice(0, 3).map((a) => (
                <span key={a} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6]">
                  {a}
                </span>
              ))}
              {form.amenities.length > 3 && <span className="text-[10px] text-[#9aa0a6]">+{form.amenities.length - 3} more</span>}
            </div>

            <button
              onClick={() => navigate('/listing-preview')}
              className="mt-3.5 w-full py-2 rounded-full text-xs font-semibold border border-[#dadce0] dark:border-[#3c4043] text-[#1a73e8] dark:text-[#8ab4f8] hover:bg-[#f8fafd] dark:hover:bg-[#202124] cursor-pointer flex items-center justify-center gap-1.5">
              Open Full Preview <ExternalLink className="w-3.5 h-3.5" />
            </button>
            {activeTab === 'slots' && form.slotGeneratorTouched && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 text-center">Unsaved slot changes — save to publish.</p>
            )}
          </div>
        </div>
      </div>

      {showPicker && (
        <MapLocationPicker
          initialLat={form.latitude}
          initialLng={form.longitude}
          onClose={() => setShowPicker(false)}
          onConfirm={picked => { applyPickedLocation(setForm, picked); setShowPicker(false); }}
        />
      )}
    </div>
  );
}

export default function MyGymPage() {
  const [gym, setGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasGym, setHasGym] = useState(false);

  useEffect(() => {
    getMyGym()
      .then(g => { setGym(g); setHasGym(true); })
      .catch(() => setHasGym(false))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <div className="w-10 h-10 rounded-full border-4 border-[#e8f0fe] dark:border-[#1a2e4c] border-t-[#1a73e8] animate-spin" />
      </div>
    );
  }

  return hasGym
    ? <EditGymForm gym={gym} onUpdated={g => setGym(g)} />
    : <CreateGymForm onCreated={g => { setGym(g); setHasGym(true); }} />;
}
